import type { MessageKey } from "./en";

/** Traditional Chinese UI catalog. Every key in {@link en} must be present. */
export const zhTW: Record<MessageKey, string> = {
  "app.title": "Rescript — 像編輯文字一樣編輯影片",
  "common.cancel": "取消",
  "common.close": "關閉",
  "common.delete": "刪除",
  "common.download": "下載",
  "common.import": "匯入",
  "common.loading": "載入中",
  "common.remove": "移除",
  "common.restore": "復原",
  "common.retry": "再試一次",
  "common.searchOrCreate": "搜尋或新增…",
  "common.settings": "設定",
  "common.system": "跟隨系統",
  "common.tools": "工具",
  "settings.appearance": "外觀",
  "settings.light": "淺色",
  "settings.dark": "深色",
  "settings.interfaceLanguage": "介面語言",
  "settings.privacy": "隱私",
  "settings.helpImprove": "協助改善應用程式",
  "settings.telemetryHelp": "傳送匿名功能使用統計與當機報告。",
  "settings.support": "支援 / 意見回饋",
  "settings.reportIssue": "回報問題",
  "settings.homepage": "首頁",
  "settings.github": "GitHub",
  "settings.followX": "在 X 上追蹤",
  "model.transcriptSource": "逐字稿來源",
  "model.language": "語言",
  "model.transcriptLanguage": "逐字稿語言",
  "model.importTranscript": "匯入逐字稿",
  "upload.recentProjects": "最近專案",
  "upload.removeRecent": "從最近專案移除",
  "upload.dropPrefix": "將影片或音訊檔拖到這裡，或",
  "upload.browse": "瀏覽",
  "upload.chooseTranscriptFirst": "先在上方選單選擇逐字稿，再拖入媒體",
  "upload.willUseTranscript": "將使用 {name} · MP4、WebM、MOV、MP3、WAV 等",
  "upload.mediaFormats": "MP4、WebM、MOV、MP3、WAV、M4A 等",
  "upload.gettingReady": "正在準備",
  "upload.gettingReadyHelp": "正在設定媒體引擎，只會發生一次。",
  "upload.unsupported": "此瀏覽器無法執行編輯器",
  "upload.unsupportedHelp":
    "編輯需要 SharedArrayBuffer，因此頁面必須是跨來源隔離。請透過 HTTPS 使用較新的 Chrome、Edge、Safari 或 Firefox。",
  "upload.unsupportedSimd": "此裝置無法執行編輯器",
  "upload.unsupportedSimdHelp": "編輯功能需要 WebAssembly SIMD，但此處理器未回報支援。在 Windows on Arm 電腦上，請更新至最新版 Rescript——目前的安裝程式包含無須模擬執行的原生 Arm64 版本。",
  "upload.transcribeTitle": "轉錄",
  "upload.transcribeText": "在本機使用 Whisper，或匯入 SRT / VTT。",
  "upload.editTitle": "編輯",
  "upload.editText": "選取文字並按刪除即可剪輯。",
  "upload.exportTitle": "匯出",
  "upload.exportText": "將完成的剪輯算出為 MP4 或 M4A。",
  "editor.chooseTranscript": "請先從來源選單選擇逐字稿檔案。",
  "editor.chooseMedia": "請選擇影片或音訊檔。",
  "editor.undo": "復原 (⌘Z)",
  "editor.redo": "重做 (⇧⌘Z)",
  "editor.export": "匯出",
  "topbar.startOver": "重新開始",
  "transcript.replace": "用 SRT、VTT 或 JSON 取代逐字稿",
  "transcript.header": "逐字稿",
  "transcript.wordDeleted": "{count} 個字",
  "transcript.wordsDeleted": "{count} 個字",
  "transcript.replaceConfirm": "要用此檔案取代目前的逐字稿嗎？",
  "transcript.invalidFile": "請選擇 SRT、VTT 或 JSON 逐字稿檔。",
  "transcript.noSpeech": "未偵測到語音。請確認檔案有音訊，或匯入逐字稿。",
  "transcript.cut": "剪除",
  "transcript.follow": "跟隨播放頭",
  "transcript.hideDeleted": "隱藏已刪除文字",
  "transcript.showDeleted": "顯示已刪除文字",
  "transcript.correct": "修正",
  "transcript.scrollWithPlayhead": "隨播放頭捲動",
  "transcript.joinSplit": "片段分割 — 點一下即可合併這些片段",
  "transcript.joinClips": "合併片段",
  "transcript.hesitation": "偵測到猶豫聲（未轉錄）— 可用「移除填充詞」剪除",
  "transcript.textLabel": "轉錄文字",
  "transcript.textHint": "方向鍵在字詞間移動並同步移動播放頭。按住 Shift 加方向鍵進行選取。Backspace 或 Delete 剪除字詞，Enter 更正，空白鍵播放或暫停，復原可找回剪除的內容。",
  "transcript.textHesitation": "［猶豫］",
  "transcript.textCut": "已剪除：{text}",
  "transcript.textNothingToCut": "沒有可剪除的內容",
  "transcript.textUndo": "復原",
  "transcript.textRedo": "重做",
  "tools.bulk": "批次清理逐字稿",
  "tools.removeFillers": "移除填充詞",
  "tools.removeFillersTitle": "從影片中剪除填充詞（\"嗯\"、\"呃\"、\"...\" 等）",
  "tools.restoreFillers": "還原填充詞",
  "tools.restoreFillersTitle": "把所有已剪除的填充詞加回來",
  "tools.removeSilences": "移除靜音",
  "tools.removeSilencesTitle": "從影片中剪除停頓和靜音（≥{seconds} 秒）",
  "tools.restoreSilences": "還原靜音",
  "tools.restoreSilencesTitle": "把所有已剪除的停頓加回來",
  "timeline.removed": "已剪除 {trimmed} — 原始長度 {duration}",
  "timeline.back5": "倒退 5 秒",
  "timeline.playPause": "播放 / 暫停（空白鍵）",
  "timeline.forward5": "前進 5 秒",
  "timeline.split": "分割",
  "timeline.splitTitle": "在播放頭位置分割片段 (S)",
  "timeline.splitDisabled": "將播放頭移到保留區域上才能分割",
  "timeline.delete": "刪除",
  "timeline.deleteTitle": "刪除選取的片段 (Delete)",
  "timeline.deleteDisabled": "請先在時間軸上選取要刪除的片段",
  "timeline.restore": "復原",
  "timeline.restoreTitle": "復原選取的剪除區域 (Delete)",
  "timeline.restoreDisabled": "請在時間軸上選取要復原的剪除或靜音區段",
  "timeline.zoomOut": "縮小",
  "timeline.fit": "符合視窗",
  "timeline.zoomIn": "放大 — 拖曳文字邊緣可微調時間",
  "timeline.joinClips": "合併這些片段（移除分割）",
  "timeline.trimStart": "修剪片段開頭",
  "timeline.trimEnd": "修剪片段結尾",
  "timeline.hesitationAdjust": "… 偵測到猶豫聲 — 拖曳邊緣調整，或用「移除填充詞」剪除",
  "timeline.hesitationCut": "… 偵測到猶豫聲 — 可用「移除填充詞」剪除",
  "timeline.dragTiming": "{word} — 拖曳邊緣可調整時間",
  "timeline.scrollZoom": "捲動以放大 / 縮小",
  "speaker.moveStart": "拖曳以移動此說話人開始的位置",
  "speaker.moveLabel": "移動說話人標籤",
  "speaker.options": "說話人選項",
  "speaker.change": "變更說話人",
  "speaker.rename": "重新命名說話人",
  "speaker.renameAction": "重新命名",
  "speaker.replace": "在專案中取代為…",
  "speaker.removeProject": "從專案移除",
  "speaker.newName": "新名稱",
  "speaker.find": "尋找說話人…",
  "speaker.noMatches": "沒有符合的說話人",
  "speaker.renameTo": "將 {current} 重新命名為 {next}",
  "speaker.button": "說話人",
  "speaker.search": "搜尋或新增…",
  "speaker.defaultName": "說話人 {number}",
  "speaker.create": "建立「{name}」",
  "export.title": "匯出",
  "export.type": "匯出類型",
  "export.video": "影片",
  "export.audio": "音訊",
  "export.transcript": "逐字稿",
  "export.subtitles": "字幕",
  "export.videoUnavailable": "純音訊專案無法匯出影片",
  "export.noAudio": "此檔案沒有音軌",
  "export.noWordsFirst": "請先轉錄或匯入逐字稿",
  "export.format": "格式",
  "export.resolution": "解析度",
  "export.original": "原始",
  "export.plainText": "TXT",
  "export.markdown": "Markdown",
  "export.statOriginal": "原始",
  "export.statCuts": "剪除",
  "export.statEdited": "成片",
  "export.includeTimestamps": "包含時間戳",
  "export.speakerLabels": "包含說話人姓名",
  "export.shortCues": "短字幕（≤5 秒）",
  "export.transcriptHelp": "帶有說話人標籤的文字，已移除剪除內容。",
  "export.transcriptHelpTimestamps":
    "帶有說話人標籤的文字，已移除剪除內容。每段以編輯後時間軸上的時間開頭。",
  "export.transcriptHelpPdf":
    "PDF 使用標準拉丁字型 — 完整 Unicode（如中日韓）請優先選擇 DOCX。",
  "export.subtitlesHelp":
    "SRT 和 VTT 使用已套用剪除的編輯後時間軸。JSON 保留完整詞表以便重新匯入。",
  "export.subtitlesHelpShortCues":
    "SRT 和 VTT 使用已套用剪除的編輯後時間軸，並拆成短句字幕（≤5 秒）。JSON 保留完整詞表以便重新匯入。",
  "export.encodingHelp": "使用 ffmpeg.wasm 重新編碼 — 較長的檔案需要一些時間。",
  "export.reexport": "以最新編輯重新匯出",
  "export.rendering": "正在瀏覽器中算出…",
  "export.downloadFile": "下載 {name}",
  "export.saveFile": "儲存 {name}…",
  "export.saving": "正在儲存…",
  "export.saveDialogTitle": "儲存匯出檔案",
  "export.savedAs": "已儲存為 {name}",
  "export.showInFinder": "在 Finder 中顯示",
  "export.showInExplorer": "在檔案總管中顯示",
  "export.showInFolder": "在資料夾中顯示",
  "export.renderingNative": "正在算出…",
  "export.encodingHelpNative": "使用 ffmpeg 重新編碼 — 較長的檔案需要一些時間。",
  "export.downloadFormat": "下載 .{format}",
  "export.exportFormat": "匯出 {format}",
  "export.timeline": "時間軸",
  "export.timelineTitle": "匯出 NLE / DAW 序列（Resolve、Premiere、Final Cut、Pro Tools、Reaper、Blender）",
  "export.nle": "格式",
  "export.frameRate": "幀率",
  "export.timelineHelp": "序列依檔名引用原始媒體 — 匯入後請在 NLE 中重新連結。",
  "export.timelineHelpAaf": "Pro Tools / Logic AAF（僅中繼資料）。",
  "export.timelineHelpFcpx": "Final Cut Pro FCPXML。",
  "export.timelineHelpPremiere": "Adobe Premiere Pro XML（xmeml）。",
  "export.timelineHelpResolve": "DaVinci Resolve XML（xmeml）。",
  "export.timelineHelpReaper": "Reaper 專案（.rpp）。",
  "export.timelineHelpSamplitude":
    "Samplitude EDL（.edl）— 也可在 Reaper 中直接開啟。",
  "export.timelineHelpBlender":
    "Blender 影片剪輯專案（.blend，Blender 5.2 以上）— 請儲存在與原始媒體相同的資料夾中。",
  "export.aafOverCap":
    "此編輯有 {count} 個片段；AAF 最多支援 {max} 個。請改用 Resolve、Premiere、Final Cut、Reaper 或 Samplitude EDL。",
  "export.preparing": "正在準備…",
  "import.reading": "正在讀取逐字稿…",
  "import.importing": "正在匯入…",
  "import.failed": "匯入失敗",
  "import.readingFile": "正在讀取檔案…",
  "import.chooseFileShort": "選擇檔案…",
  "import.chooseFile": "選擇 SRT、VTT 或 JSON 逐字稿",
  "import.selected": "已選擇 {name}",
  "banner.faster": "想要更快轉錄和匯出？",
  "banner.getDesktop": "取得 Rescript 桌面應用程式",
  "banner.downloadFor": "下載 {platform} 版",
  "banner.dismiss": "關閉",
  "social.githubRepo": "GitHub 儲存庫",
  "social.discordServer": "Discord 伺服器",
  "social.xProfile": "X 個人檔案",
  "globalError.title": "Rescript — 發生錯誤",
  "globalError.heading": "發生錯誤",
  "globalError.body":
    "編輯器遇到未預期的錯誤。你已儲存的專案仍在此裝置上，重新載入後應該會恢復。",
  "progress.loadingMedia": "正在載入媒體…",
  "progress.loadingMediaEngine": "正在載入媒體引擎…",
  "progress.extractingAudio": "正在擷取音訊…",
  "progress.preparingMedia": "正在準備媒體…",
  "progress.loadingSpeechModel": "正在載入語音模型…",
  "progress.loadingSpeechCache": "正在從快取載入語音模型…",
  "progress.downloadingSpeech": "正在下載語音模型…",
  "progress.gpuFallback": "GPU 中斷 — 正在改用 CPU 繼續…",
  "progress.detectingSpeech": "正在偵測語音…",
  "progress.transcribing": "正在轉錄…",
  "progress.loadingAlignCache": "正在從快取載入對齊模型…",
  "progress.downloadingAlign": "正在下載對齊模型…",
  "progress.aligning": "正在對齊文字…",
  "progress.speakers": "正在辨識說話人…",
  "error.selectModel": "請選擇要用來轉錄的語音模型。",
  "error.workerCrashed": "轉錄 worker 已當機。",
  "error.mediaEngineNetwork": "無法載入媒體引擎 — 連線中斷。請檢查網路後再試一次。",
  "error.mediaEngineStalled": "媒體引擎停止回應，已重新啟動。請關閉其他應用程式與分頁以釋放記憶體，然後再試一次。",
  "error.mediaEngineNotReady": "媒體引擎尚未就緒，請重新整理頁面後再試一次。",
  "error.simdUnsupported": "此處理器不支援媒體引擎所需的 WebAssembly SIMD。在 Windows on Arm 電腦上，請更新至最新版 Rescript——目前的安裝程式包含無須模擬執行的原生 Arm64 版本。",
  "error.processFile": "無法處理此檔案。",
  "error.extractAudio": "無法從此檔案擷取音訊。",
  "error.nothingToExport": "所有內容都已刪除 — 沒有可匯出的內容。",
  "error.videoExport": "算出影片時匯出失敗。",
  "error.audioExport": "算出音訊時匯出失敗。",
  "error.exportSave": "無法儲存匯出的檔案。",
  "error.diskFull": "磁碟空間不足，無法完成。請釋放一些空間後再試一次。",
  "error.export": "匯出失敗。",
  "error.timelineExport": "時間軸匯出失敗。",
  "error.timelineEmpty": "時間軸上沒有可放入的內容。",
  "error.aafTemplate": "無法載入 AAF 匯出範本。",
  "error.emptyTranscript": "該逐字稿檔案是空的。",
  "error.noTimedWords": "該逐字稿中找不到帶時間的文字。",
  "error.parseJson": "無法解析該 JSON 逐字稿。",
  "error.jsonShape": 'JSON 必須是文字陣列，或 { "words": [...] }。',
  "error.noWords": "沒有可匯出的文字。",
  "error.projectMissing": "該專案已不在已儲存專案中。",
  "error.openProject": "無法開啟該專案。",
  "error.removeProject": "無法移除該專案。",
  "error.readTranscript": "無法讀取該逐字稿。",
  "error.clearRecent": "無法清除最近專案。",
  "error.modelDownload":
    "無法完成語音模型下載 — 連線中斷。請檢查網路後再試一次；已下載完成的部分會保留。",
  "error.gpuReset": "GPU 重置時轉錄中斷（常見於鎖定螢幕後）。請再試一次。",
  "confirm.clearRecent": "要移除所有最近專案嗎？已儲存的編輯也會被刪除。",
};
